import React, { useRef, useEffect, useState } from 'react';
import './VideoPlayer.css';

const VideoPlayer = ({
  src,
  autoPlay = true,
  playsInline = true,
  loop = false,
  muted = true,
  loading = "eager",
  style = {},
  onEnded,
  disablePictureInPicture = true,
  ecgOff = false,
  children,
  ...props
}) => {
  const videoRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [opacity, setOpacity] = useState(ecgOff ? 0 : 0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsReady(true);
      setOpacity(ecgOff ? 0 : 1);
    };

    const handleCanPlay = () => {
      setIsReady(true);
      setOpacity(ecgOff ? 0 : 1);
    };

    const handleLoadStart = () => {
      setOpacity(ecgOff ? 0 : 0);
    };

    const handleLoadedMetadata = () => {
      // Video is ready, set opacity immediately
      setOpacity(ecgOff ? 0 : 1);
    };

    const handleProgress = () => {
      // Keep opacity at 0 during loading/progress to prevent icons
      setOpacity(ecgOff ? 0 : 0);
    };

    // Add comprehensive event listeners
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('progress', handleProgress);

    // Force video to play immediately and hide any potential icons
    if (autoPlay) {
      video.play().catch(error => {
        console.log('Auto-play was prevented:', error);
      });
    }

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('progress', handleProgress);
    };
  }, [src, autoPlay, ecgOff]);

  useEffect(() => {
    setOpacity(ecgOff ? 0 : (isReady ? 1 : 0));
  }, [ecgOff, isReady]);

  const defaultStyle = {
    width: "90%",
    margin: "auto",
    transition: 'opacity 0.1s ease-out',
    backgroundColor: 'transparent',
    ...style
  };

  return (
    <>
      <video
        ref={videoRef}
        className={isReady ? 'ready' : ''}
        src={src}
        autoPlay={autoPlay}
        playsInline={playsInline}
        loop={loop}
        muted={muted}
        loading={loading}
        style={defaultStyle}
        onEnded={onEnded}
        disablePictureInPicture={disablePictureInPicture}
        controls={false}
        preload="auto"
        poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='0'%3E%3C/svg%3E"
        {...props}
      />
      {children}
    </>
  );
};

export default VideoPlayer;
